/**
 * Pointing the camera at cells (docs/ux.md §10): centring on a unit, fitting a fleet, and
 * following the action without fighting a player who is moving the map themselves.
 */
import type { Camera } from './camera.ts';
import { CELL, cellCenter } from './geometry.ts';
import { projection, toView, type ProjectionKind } from './projection.ts';

/** When the player last panned or zoomed, per camera. */
const lastUserInput = new WeakMap<Camera, number>();

/** Called by the board's input on every pan, zoom and drag. */
export function markUserInput(camera: Camera): void {
  lastUserInput.set(camera, performance.now());
}

/** U-VIEW-07: the camera keeps still for this long after the player moved it. */
export const USER_INPUT_GRACE_MS = 3000;

const viewOf = (kind: ProjectionKind, cell: string) => {
  const c = cellCenter(cell);
  return toView(projection(kind), c.x, c.y);
};

/** Centre on a cell, keeping the zoom (U-SEL-04). */
export function centerOnCell(camera: Camera, kind: ProjectionKind, cell: string, animate = true): void {
  const v = viewOf(kind, cell);
  camera.centerOn(v.x, v.y, animate);
}

/** Whether a cell's centre is on screen, at least `margin` CSS px from the edges. */
export function cellOnScreen(camera: Camera, kind: ProjectionKind, cell: string, margin = 48): boolean {
  const v = viewOf(kind, cell);
  const s = camera.worldToScreen(v.x, v.y);
  const { width, height } = camera.viewportSize;
  return s.x >= margin && s.y >= margin && s.x <= width - margin && s.y <= height - margin;
}

/**
 * Show all `cells` (U-FLEET-05): if they are already on screen nothing moves, otherwise the
 * camera zooms out (never in) to fit them.
 */
export function fitCells(camera: Camera, kind: ProjectionKind, cells: readonly string[]): void {
  if (cells.length === 0 || cells.every((c) => cellOnScreen(camera, kind, c, 16))) return;
  const points = cells.map((c) => viewOf(kind, c));
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const { width, height } = camera.viewportSize;
  const pad = CELL * 2;
  const zoom = Math.min(width / (x1 - x0 + pad), height / (y1 - y0 + pad), camera.getSnapshot().zoom);
  camera.flyTo({ x: (x0 + x1) / 2, y: (y0 + y1) / 2, zoom }, 300);
}

export type Follow = 'visible' | 'moved' | 'held';

/**
 * U-VIEW-07: bring the action into view, gently, unless it already is (`visible`) or the
 * player has just moved the map (`held`: the caller may offer a "Show" chip instead).
 */
export function follow(camera: Camera, kind: ProjectionKind, cell: string, animate = true): Follow {
  if (cellOnScreen(camera, kind, cell)) return 'visible';
  const last = lastUserInput.get(camera) ?? -Infinity;
  if (performance.now() - last < USER_INPUT_GRACE_MS) return 'held';
  const v = viewOf(kind, cell);
  if (animate) camera.flyTo({ ...camera.getSnapshot(), x: v.x, y: v.y }, 400);
  else camera.centerOn(v.x, v.y);
  return 'moved';
}

/**
 * U-VIEW-05: after the projection changed, put `cell` back where it was on screen, so the
 * point of interest stays put when switching between the chart and the perspective.
 */
export function keepOnScreen(
  camera: Camera,
  kind: ProjectionKind,
  cell: string,
  screen: { x: number; y: number },
): void {
  const v = viewOf(kind, cell);
  camera.centerOn(v.x, v.y);
  const now = camera.worldToScreen(v.x, v.y);
  camera.panBy(screen.x - now.x, screen.y - now.y);
}

/** Where a cell is on screen now, in the given projection. */
export function cellScreen(camera: Camera, kind: ProjectionKind, cell: string): { x: number; y: number } {
  const v = viewOf(kind, cell);
  return camera.worldToScreen(v.x, v.y);
}

/** A part of the board's screen, CSS px from its top left corner. */
export interface ScreenArea {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * U-CTX-01: move the camera the least it takes for `cells` (a selected ship and its reach) to
 * lie inside `area`, the part of the board no card or bar covers. What is already inside stays
 * put; what does not fit is centred in the area. Never zooms. True when the camera moved.
 */
export function keepInside(
  camera: Camera,
  kind: ProjectionKind,
  cells: readonly string[],
  area: ScreenArea,
  animate = true,
): boolean {
  if (cells.length === 0) return false;
  const view = camera.getSnapshot();
  // Half a cell round each centre, so whole cells show, not only their middles.
  const pad = (CELL * view.zoom) / 2;
  const points = cells.map((c) => cellScreen(camera, kind, c));
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const shift = (lo: number, hi: number, min: number, max: number) => {
    if (hi - lo > max - min) return (min + max) / 2 - (lo + hi) / 2;
    if (lo < min) return min - lo;
    if (hi > max) return max - hi;
    return 0;
  };
  const dx = shift(Math.min(...xs) - pad, Math.max(...xs) + pad, area.left, area.right);
  const dy = shift(Math.min(...ys) - pad, Math.max(...ys) + pad, area.top, area.bottom);
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return false;
  const target = { ...view, x: view.x - dx / view.zoom, y: view.y - dy / view.zoom };
  if (animate) camera.flyTo(target, 300);
  else camera.centerOn(target.x, target.y);
  return true;
}
